import { useMenu } from '@happyspace/sdk/restaurant';
import { MENU, SIGNATURES, ITEM_COUNT, type MenuSection, type MenuItem, type Tag } from '../data/menu';

/* ------------------------------------------------------------------
   Live menu, the formal Happyspace way.

   Reads the restaurant's menu from the Happyspace Restaurant SDK
   (`useMenu()` → resolves window.SITE_CONFIG.businessId → Menu V2),
   and reshapes the flat SDK items into this site's curated section
   structure — grouping by category and preserving each section's
   kicker/blurb/order from the static MENU as metadata.

   If the SDK is still loading or returns nothing, it falls back to the
   bundled static menu so the site never renders empty.
   ------------------------------------------------------------------ */

function tagOf(item: any): Tag {
  const t = (item?.tags || []).find((x: string) => x === 'veg' || x === 'egg' || x === 'nonveg');
  if (t) return t as Tag;
  return item?.dietaryFlags?.isVegetarian ? 'veg' : 'nonveg';
}

export interface MenuData {
  sections: MenuSection[];
  signatures: MenuItem[];
  itemCount: number;
  loading: boolean;
  live: boolean;
}

export function useMenuData(): MenuData {
  const sdk = useMenu() as { items?: any[]; loading?: boolean };
  const items = sdk?.items;

  // Fallback to the bundled menu while loading or if the SDK has no items yet.
  if (!items || items.length === 0) {
    return { sections: MENU, signatures: SIGNATURES, itemCount: ITEM_COUNT, loading: !!sdk?.loading, live: false };
  }

  const byCat = new Map<string, any[]>();
  for (const it of items) {
    const c = it?.category || 'Menu';
    if (!byCat.has(c)) byCat.set(c, []);
    byCat.get(c)!.push(it);
  }

  const sections: MenuSection[] = MENU.map((meta) => {
    const src = byCat.get(meta.title);
    if (!src || src.length === 0) return meta;
    const mapped: MenuItem[] = src
      .slice()
      .sort((a: any, b: any) => (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0))
      .map((it: any) => ({
        name: it?.title ?? '',
        price: typeof it?.basePrice === 'number' ? it.basePrice : 0,
        tag: tagOf(it),
        signature: !!it?.featured || (it?.tags || []).includes('signature'),
        desc: it?.description || undefined,
        weekend: (it?.tags || []).includes('weekend'),
        image: it?.image || undefined,
        raw: it,
      }));
    return { ...meta, items: mapped };
  });

  const signatures = sections.flatMap((s) => s.items).filter((i) => i.signature);
  const itemCount = sections.reduce((n, s) => n + s.items.length, 0);
  return { sections, signatures, itemCount, loading: false, live: true };
}
